import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, Download, Trash2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { config } from '@/src/config';
import { useFormattedDate } from '../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

interface Voicemail {
  id: number;
  recording_sid: string;
  from_number: string;
  recording_url: string;
  duration_seconds: number;
  is_read: boolean;
  received_at: string;
  transcription_text?: string;
  referral_id?: string;
}

interface VoicemailPlayerEnhancedProps {
  referralId?: string;
}

export const VoicemailPlayerEnhanced: React.FC<VoicemailPlayerEnhancedProps> = ({
  referralId
}) => {
  const { fmt } = useFormattedDate();
  const [voicemails, setVoicemails] = useState<Voicemail[]>([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState<number | null>(null);
  const [progress, setProgress] = useState<{ [key: number]: number }>({});
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const audioRefs = useRef<{ [key: number]: HTMLAudioElement }>({});

  useEffect(() => {
    fetchVoicemails();
  }, [referralId]);

  const fetchVoicemails = async () => {
    try {
      setLoading(true);

      let url = `${config.api.baseUrl}/api/voicemails?`;
      if (referralId) url += `referral_id=${referralId}`;

      const response = await authFetch(url);

      const data = await response.json();
      setVoicemails(data.voicemails || []);

    } catch (error) {
      console.error('Failed to fetch voicemails:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePlayPause = (voicemail: Voicemail) => {
    const audio = audioRefs.current[voicemail.id];
    if (!audio) return;

    if (playing === voicemail.id) {
      audio.pause();
      setPlaying(null);
    } else {
      // Pause any other playing audio
      Object.entries(audioRefs.current).forEach(([id, audioEl]) => {
        if (parseInt(id) !== voicemail.id) {
          audioEl.pause();
        }
      });

      audio.play();
      setPlaying(voicemail.id);

      // Mark as read
      if (!voicemail.is_read) {
        markAsRead(voicemail.id);
      }
    }
  };

  const handleTimeUpdate = (voicemailId: number, audio: HTMLAudioElement) => {
    const progressPercent = (audio.currentTime / audio.duration) * 100;
    setProgress(prev => ({ ...prev, [voicemailId]: progressPercent }));
  };

  const handleSeek = (voicemailId: number, percent: number) => {
    const audio = audioRefs.current[voicemailId];
    if (audio) {
      audio.currentTime = (percent / 100) * audio.duration;
    }
  };

  const markAsRead = async (voicemailId: number) => {
    try {
      await authFetch(`${config.api.baseUrl}/api/voicemails/${voicemailId}/mark-read`, {
        method: 'POST'
      });

      setVoicemails(prev =>
        prev.map(v => v.id === voicemailId ? { ...v, is_read: true } : v)
      );

    } catch (error) {
      console.error('Failed to mark voicemail as read:', error);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (number: string) => {
    const cleaned = number.replace(/\D/g, '');
    const match = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return number;
  };

  const unreadCount = voicemails.filter(v => !v.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" />
          Voicemails ({voicemails.length})
        </h3>
        {unreadCount > 0 && (
          <span className="px-3 py-1 bg-red-500 text-white rounded-full text-xs font-semibold animate-pulse">
            {unreadCount} new
          </span>
        )}
      </div>

      {voicemails.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Mail className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No voicemails yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {voicemails.map((voicemail) => (
            <div
              key={voicemail.id}
              className={`p-4 rounded-lg border-2 transition-all ${
                !voicemail.is_read
                  ? 'border-blue-400 bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => togglePlayPause(voicemail)}
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center
                      transition-all transform hover:scale-105
                      ${playing === voicemail.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      }
                    `}
                  >
                    {playing === voicemail.id ? (
                      <Pause className="w-5 h-5" />
                    ) : (
                      <Play className="w-5 h-5 ml-0.5" />
                    )}
                  </button>

                  <div>
                    <p className="font-semibold text-gray-900">
                      {formatPhoneNumber(voicemail.from_number)}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Volume2 className="w-3 h-3" />
                      <span>{formatDuration(voicemail.duration_seconds)}</span>
                      <span className="text-gray-400">•</span>
                      <span>{fmt.short(voicemail.received_at)}</span>
                      <span>{fmt.time(voicemail.received_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!voicemail.is_read && (
                    <span className="px-2 py-1 bg-blue-600 text-white text-xs font-semibold rounded">
                      NEW
                    </span>
                  )}
                  <a
                    href={voicemail.recording_url}
                    download
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Audio Element */}
              <audio
                ref={el => {
                  if (el) audioRefs.current[voicemail.id] = el;
                }}
                src={voicemail.recording_url}
                onTimeUpdate={(e) => handleTimeUpdate(voicemail.id, e.currentTarget)}
                onEnded={() => setPlaying(null)}
                volume={volume}
                muted={muted}
              />

              {/* Progress Bar */}
              <div className="mb-3">
                <div
                  className="h-2 bg-gray-200 rounded-full overflow-hidden cursor-pointer"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const percent = ((e.clientX - rect.left) / rect.width) * 100;
                    handleSeek(voicemail.id, percent);
                  }}
                >
                  <div
                    className="h-full bg-blue-600 transition-all"
                    style={{ width: `${progress[voicemail.id] || 0}%` }}
                  />
                </div>
              </div>

              {/* Transcription */}
              {voicemail.transcription_text && (
                <div className="mt-3 p-3 bg-gray-100 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1 font-medium">TRANSCRIPTION</p>
                  <p className="text-sm text-gray-800 leading-relaxed">
                    {voicemail.transcription_text}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Global Volume Control */}
      {voicemails.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <button
            onClick={() => setMuted(!muted)}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {muted ? (
              <VolumeX className="w-4 h-4 text-gray-600" />
            ) : (
              <Volume2 className="w-4 h-4 text-gray-600" />
            )}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="flex-1"
          />
          <span className="text-sm text-gray-600 w-12 text-right">
            {Math.round(volume * 100)}%
          </span>
        </div>
      )}
    </div>
  );
};
